import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { LaunchDarklyService } from 'src/app/config/services/launch-darkly.service';
import { LaunchDarklyFlag } from 'src/app/config/state/launch-darkly/launch-darkly.models';
import { ClientRequestsState, SetSelectedRequestItemId } from '../../../../../state';
import { ClientRequestDocumentFileDisplay } from '../../../../../models';
import { DATA_SELECTOR_INJECTION_TOKEN } from '@aiwyn/shared';

interface FlaggedFileWithRequestItem extends ClientRequestDocumentFileDisplay {
  requestItemName: string;
  isFlagged: boolean;
}

@Component({
  selector: 'awn-flagged-files-summary',
  templateUrl: './flagged-files-summary.component.html',
  styleUrls: ['./flagged-files-summary.component.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'flagged-files-summary',
    },
  ],
  standalone: false,
})
export class FlaggedFilesSummaryComponent {
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  public isMobileMode = input<boolean>(false);

  private readonly _store = inject(Store);
  private readonly _launchDarklyService = inject(LaunchDarklyService);

  private readonly MAX_DISPLAYED_FILES = 3;

  protected isFileFlaggingEnabled = toSignal(
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_FILE_FLAGGING),
    { initialValue: false }
  );

  // Core data from selector (no presentational logic)
  protected flaggedFilesWithRequestItems = toSignal(
    this._store.select(ClientRequestsState.getFlaggedFilesWithRequestItems),
    { initialValue: [] as FlaggedFileWithRequestItem[] }
  );

  // Presentational logic (computed)
  protected flaggedFilesSummary = computed(() => {
    if (!this.isFileFlaggingEnabled()) {
      return null;
    }

    const flaggedFiles = this.flaggedFilesWithRequestItems();

    if (flaggedFiles.length === 0) {
      return null;
    }

    // PRESENTATIONAL LOGIC: sorting, slicing, counting
    const sortedFiles = [...flaggedFiles].sort((a, b) => a.name.localeCompare(b.name));

    const uniqueRequestItemIds = new Set(
      flaggedFiles
        .filter(file => file.requestItemId !== null)
        .map(file => file.requestItemId)
    );

    return {
      totalFiles: flaggedFiles.length,
      uniqueRequestItems: uniqueRequestItemIds.size,
      displayFiles: sortedFiles.slice(0, this.MAX_DISPLAYED_FILES),
      remainingCount: Math.max(0, sortedFiles.length - this.MAX_DISPLAYED_FILES)
    };
  });

  // Click handler to select and scroll to request item
  protected selectRequestItem(file: FlaggedFileWithRequestItem): void {
    if (file.requestItemId) {
      this._store.dispatch(new SetSelectedRequestItemId(file.requestItemId));
    }
  }
}
